(() => {
    'use strict';

    const svgNamespace = 'http://www.w3.org/2000/svg';
    const htmlNamespace = 'http://www.w3.org/1999/xhtml';
    const backgroundProperties = [
        'backgroundColor', 'backgroundImage', 'backgroundSize', 'backgroundPosition',
        'backgroundRepeat', 'backgroundAttachment', 'backgroundOrigin',
        'backgroundClip', 'backgroundBlendMode'
    ];
    const surfaces = new Map();
    let owners = new Set();
    let nextId = 0;
    let frame;
    const property = (style, name) => style.getPropertyValue(`--section-mask-${name}`).trim();
    const observer = new ResizeObserver(() => schedule());

    function schedule() {
        if (!frame) frame = requestAnimationFrame(update);
    }

    function discover() {
        document.querySelectorAll('[data-plug-section-mask]').forEach(marker => {
            const config = JSON.parse(marker.textContent);
            const section = document.getElementById(`shopify-section-${config.sectionId}`);
            if (!section) return;
            config.targets.forEach(selector => {
                const targets = selector ? section.querySelectorAll(selector) : [section];
                targets.forEach(target => {
                    if (surfaces.has(target)) return;
                    surfaces.set(target, { section, anchor: null, edges: {} });
                    observer.observe(target);
                });
            });
        });
        schedule();
    }

    function createSvg(record, edge) {
        if (!record.anchor) {
            record.anchor = document.createElement('div');
            record.anchor.className = 'plug-section-mask-anchor';
            record.anchor.setAttribute('aria-hidden', 'true');
            record.anchor.inert = true;
            record.section.after(record.anchor);
        }
        const svg = document.createElementNS(svgNamespace, 'svg');
        svg.classList.add('plug-section-mask-extension');
        svg.dataset.edge = edge;
        svg.setAttribute('preserveAspectRatio', 'none');
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('aria-hidden', 'true');
        record.anchor.append(svg);
        const path = document.createElementNS(svgNamespace, 'path');
        path.setAttribute('fill-rule', 'evenodd');
        svg.append(path);
        return { svg, path, clipId: `plug-section-edge-${++nextId}` };
    }

    function renderSvg(drawing, edge, style, box, depth) {
        const { svg, path, clipId } = drawing;
        const viewbox = property(style, `${edge}-viewbox`);
        const [x, y, width, height] = viewbox.split(/\s+/).map(Number);
        const sourcePath = JSON.parse(property(style, `${edge}-path`));
        const inverse = property(style, `${edge}-inverse`) === '1';
        // Extend only top SVGs into their section without shifting the jagged
        // boundary. Expand the viewBox by the same physical amount as the SVG.
        const overscan = edge === 'top' && depth > 0 ? 2 : 0;
        const drawnHeight = height + (overscan ? overscan * height / depth : 0);
        // Some bottom-source masks have a single, explicit inverted top outline.
        // Using one filled polygon avoids the shared rectangle/subtracted-path
        // boundary that can produce a hairline at certain SVG pixel scales.
        const topOutlineSetting = edge === 'top' && inverse
            ? property(style, 'top-direct-outline')
            : '';
        const topOutline = topOutlineSetting && topOutlineSetting !== '""'
            ? JSON.parse(topOutlineSetting)
            : '';
        const outline = topOutline
            ? `${topOutline} L${x + width} ${y + drawnHeight} L${x} ${y + drawnHeight} Z`
            : inverse
                ? `M${x} ${y}h${width}v${drawnHeight}h${-width}Z ${sourcePath}`
                : sourcePath;
        svg.setAttribute('viewBox', `${x} ${y} ${width} ${drawnHeight}`);
        svg.style.height = `${depth + overscan}px`;
        path.setAttribute('d', outline);

        if (style.backgroundImage === 'none') {
            // Render the actual SVG path in the section color. No CSS mask is used.
            path.setAttribute('fill', style.backgroundColor);
            if (drawing.background) {
                drawing.background.remove();
                drawing.defs.remove();
                drawing.background = null;
            }
            return;
        }

        // Gradients and textures are painted inside the same SVG outline. The
        // foreignObject is clipped as artwork, never applied as a section mask.
        path.setAttribute('fill', 'none');
        if (!drawing.background) {
            drawing.defs = document.createElementNS(svgNamespace, 'defs');
            const clip = document.createElementNS(svgNamespace, 'clipPath');
            clip.id = clipId;
            clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
            drawing.clipPath = document.createElementNS(svgNamespace, 'path');
            drawing.clipPath.setAttribute('clip-rule', 'evenodd');
            clip.append(drawing.clipPath);
            drawing.defs.append(clip);
            svg.prepend(drawing.defs);
            drawing.background = document.createElementNS(svgNamespace, 'foreignObject');
            drawing.background.setAttribute('clip-path', `url(#${clipId})`);
            const container = document.createElementNS(htmlNamespace, 'div');
            container.className = 'plug-section-mask-extension__background';
            drawing.paint = document.createElementNS(htmlNamespace, 'div');
            drawing.paint.className = 'plug-section-mask-extension__paint';
            container.append(drawing.paint);
            drawing.background.append(container);
            svg.append(drawing.background);
        }
        drawing.clipPath.setAttribute('d', outline);
        Object.entries({ x, y, width, height: drawnHeight }).forEach(([name, value]) => {
            drawing.background.setAttribute(name, value);
        });
        backgroundProperties.forEach(name => { drawing.paint.style[name] = style[name]; });
        drawing.paint.style.height = `${Math.max(drawnHeight, box.height * width / box.width)}px`;
        drawing.paint.style.top = edge === 'bottom' ? '0px' : 'auto';
        drawing.paint.style.bottom = edge === 'top' ? '0px' : 'auto';
    }

    function update() {
        frame = null;
        const active = [];
        const nextOwners = new Set();
        surfaces.forEach((record, target) => {
            if (!target.isConnected) {
                if (record.anchor) record.anchor.remove();
                observer.unobserve(target);
                surfaces.delete(target);
                return;
            }
            const style = getComputedStyle(target);
            const box = target.getBoundingClientRect();
            if (box.width && box.height && style.visibility !== 'hidden' && property(style, 'active') === '1') {
                active.push({ target, record, style, box });
            } else if (record.anchor) {
                record.anchor.hidden = true;
            }
        });
        active.sort((a, b) => a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

        active.forEach(({ record, style, box }, index) => {
            let extended = false;
            ['top', 'bottom'].forEach(edge => {
                const ratio = Number(property(style, `${edge}-ratio`));
                if (property(style, `${edge}-mode`) !== 'extended' || !ratio) {
                    if (record.edges[edge]) record.edges[edge].svg.setAttribute('hidden', '');
                    return;
                }
                extended = true;
                const drawing = record.edges[edge] || (record.edges[edge] = createSvg(record, edge));
                drawing.svg.removeAttribute('hidden');
                const opposite = edge === 'top' ? 'bottom' : 'top';
                const neighbor = active[index + (edge === 'top' ? -1 : 1)];
                const paired = neighbor && property(neighbor.style, `${opposite}-mode`) === 'normal' &&
                    property(neighbor.style, `${opposite}-pattern`) === property(style, `${edge}-pattern`);
                const referenceHeight = paired ? neighbor.box.height : box.height;
                renderSvg(drawing, edge, style, box, Math.min(innerWidth * ratio / 100, referenceHeight / 2));
            });
            if (!record.anchor) return;
            record.anchor.hidden = !extended;
            if (!extended) return;
            nextOwners.add(record.section);
            const parent = record.anchor.offsetParent;
            const initialOrigin = !parent || (parent === document.body && getComputedStyle(parent).position === 'static');
            const origin = initialOrigin ? { left: -scrollX, top: -scrollY } : parent.getBoundingClientRect();
            Object.assign(record.anchor.style, {
                left: `${box.left - origin.left + (initialOrigin ? 0 : parent.scrollLeft - parent.clientLeft)}px`,
                top: `${box.top - origin.top + (initialOrigin ? 0 : parent.scrollTop - parent.clientTop)}px`,
                width: `${box.width}px`,
                height: `${box.height}px`,
                opacity: style.opacity
            });
        });
        owners.forEach(section => {
            if (!nextOwners.has(section)) section.classList.remove('plug-section-mask-owner', 'plug-section-mask-owner--relative');
        });
        nextOwners.forEach(section => {
            if (getComputedStyle(section).position === 'static') section.classList.add('plug-section-mask-owner--relative');
            section.classList.add('plug-section-mask-owner');
        });
        owners = nextOwners;
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', discover);
    else discover();
    addEventListener('resize', schedule, { passive: true });
    addEventListener('scroll', () => { if (owners.size) schedule(); }, { passive: true });
    addEventListener('load', schedule);
    document.addEventListener('transitionend', schedule, true);
    document.addEventListener('animationend', schedule, true);
    document.addEventListener('shopify:section:load', discover);
    document.addEventListener('shopify:section:reorder', schedule);
    document.addEventListener('shopify:section:unload', schedule);
})();
